import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { api } from '../../../lib/api';

type B2BPricing = {
  model: 'tiers' | 'per_student_year';
  price_per_student_year_eur: number;
};

type B2BSchoolRow = {
  school_id: number;
  school_name: string;
  county: string | null;
  city: string | null;
  users_count: number;
  students_count: number;
  students_fte: number;
  pricing_model: 'tiers' | 'per_student_year';
  default_price_per_student_year_eur: number;
  custom_price_per_student_year_eur: number | null;
  price_per_student_year_eur: number;
  annual_price_eur: number;
  active: boolean;
  billing_status: 'unbilled' | 'billed' | 'pending' | 'paid';
  tier_id: number | null;
  custom_monthly_price_eur: number | null;
  billing_email: string | null;
  note: string | null;
  starts_at: string | null;
  ends_at: string | null;
};

function fmtEUR(v: number | null | undefined) {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  try {
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'EUR' }).format(v);
  } catch {
    return `${Number(v).toFixed(2)} EUR`;
  }
}

function cleanNumber(s: string) {
  const v = String(s || '').trim().replace(',', '.');
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function filenameFromHeader(header: string | undefined, fallback: string) {
  const h = String(header || '');
  const m = /filename=\"?([^\";]+)\"?/i.exec(h);
  return (m?.[1] || '').trim() || fallback;
}

async function downloadCsv(path: string, fallbackName: string) {
  const resp = await api.get(path, { responseType: 'blob' as any });
  const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFromHeader((resp.headers as any)?.['content-disposition'], fallbackName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function B2BSubscriptionsPanel() {
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState<B2BSchoolRow[]>([]);
  const [pricing, setPricing] = useState<B2BPricing | null>(null);
  const [pricingDraft, setPricingDraft] = useState<{ model: 'tiers' | 'per_student_year'; price_per_student_year_eur: string }>({
    model: 'per_student_year',
    price_per_student_year_eur: '5',
  });
  const [pricingBusy, setPricingBusy] = useState(false);

  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pricingResp, schoolsResp] = await Promise.all([
        api.get('/api/admin/platform/b2b/pricing'),
        api.get('/api/admin/platform/b2b/schools'),
      ]);
      const p = (pricingResp.data || null) as any;
      const nextPricing: B2BPricing = {
        model: (p?.model || 'per_student_year') as any,
        price_per_student_year_eur: Number(p?.price_per_student_year_eur || 0),
      };
      setPricing(nextPricing);
      setPricingDraft({
        model: nextPricing.model,
        price_per_student_year_eur: String(nextPricing.price_per_student_year_eur ?? 0),
      });
      setSchools(Array.isArray(schoolsResp.data) ? (schoolsResp.data as any) : []);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca abonamentele B2B.');
      setSchools([]);
      setPricing(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const savePricing = useCallback(async () => {
    const price = cleanNumber(pricingDraft.price_per_student_year_eur);
    if (pricingDraft.model === 'per_student_year' && (price === null || price < 0)) {
      alert('Preț invalid.');
      return;
    }
    setPricingBusy(true);
    try {
      await api.put('/api/admin/platform/b2b/pricing', {
        model: pricingDraft.model,
        price_per_student_year_eur: pricingDraft.model === 'per_student_year' ? Number(price || 0) : null,
      });
      await loadAll();
      alert('Salvat.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva prețul.');
    } finally {
      setPricingBusy(false);
    }
  }, [loadAll, pricingDraft.model, pricingDraft.price_per_student_year_eur]);

  const filteredSchools = useMemo(() => {
    const needle = (q || '').trim().toLowerCase();
    if (!needle) return schools;
    return schools.filter((s) => String(s.school_name || '').toLowerCase().includes(needle));
  }, [schools, q]);

  useEffect(() => {
    setPage(1);
  }, [q]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredSchools.length / pageSize)), [filteredSchools.length]);
  const pagedSchools = useMemo(() => {
    const p = Math.min(Math.max(1, page), totalPages);
    const start = (p - 1) * pageSize;
    return filteredSchools.slice(start, start + pageSize);
  }, [filteredSchools, page, totalPages]);

  const exportSchoolsCsv = useCallback(async () => {
    try {
      await downloadCsv('/api/admin/platform/b2b/schools.csv', 'b2b_schools.csv');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta CSV.');
    }
  }, []);

  const updateLocalSchool = useCallback((school_id: number, patch: Partial<B2BSchoolRow>) => {
    setSchools((prev) => prev.map((s) => (s.school_id === school_id ? { ...s, ...patch } : s)));
  }, []);

  const saveSchool = useCallback(
    async (row: B2BSchoolRow) => {
      try {
        await api.put(`/api/admin/platform/b2b/schools/${row.school_id}`, {
          active: !!row.active,
          tier_id: row.tier_id,
          custom_monthly_price_eur: row.custom_monthly_price_eur,
          custom_price_per_student_year_eur: row.custom_price_per_student_year_eur,
          billing_status: row.billing_status,
          billing_email: row.billing_email,
          note: row.note,
          starts_at: row.starts_at || null,
          ends_at: row.ends_at || null,
        });
        await loadAll();
        alert('Salvat.');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut salva.');
      }
    },
    [loadAll]
  );

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-foreground">Abonamente școli (B2B)</div>
          <div className="text-sm text-muted-foreground">Tarifare per elev/an (plata se face separat, pe bază de contract).</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" iconName="Download" disabled={loading} onClick={() => void exportSchoolsCsv()}>
            Export CSV
          </Button>
          <Button variant="outline" iconName="RefreshCw" onClick={() => void loadAll()} disabled={loading}>
            Reîncarcă
          </Button>
        </div>
      </div>

      <div className="mt-4 border border-border rounded-lg p-4 bg-background">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-foreground">Tarifare B2B</div>
            <div className="text-sm text-muted-foreground">Setezi prețul per elev, anual. Totalul per școală se calculează automat.</div>
          </div>
          <Button variant="default" iconName="Save" disabled={pricingBusy || loading} onClick={() => void savePricing()}>
            Salvează tarif
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Select
            label="Model"
            value={pricingDraft.model}
            options={[
              { value: 'per_student_year', label: 'Per elev / anual' },
              { value: 'tiers', label: 'Praguri utilizatori (legacy)' },
            ]}
            onChange={(v) => setPricingDraft((p) => ({ ...p, model: (String(v) as any) || 'per_student_year' }))}
          />
          <Input
            label="Preț (EUR / elev / an)"
            value={pricingDraft.price_per_student_year_eur}
            onChange={(e) => setPricingDraft((p) => ({ ...p, price_per_student_year_eur: e.target.value }))}
            placeholder="Ex: 5"
            disabled={pricingDraft.model !== 'per_student_year'}
          />
          <div className="flex items-end">
            <div className="text-xs text-muted-foreground">
              {pricing?.model === 'per_student_year'
                ? `Preț curent: ${fmtEUR(pricing.price_per_student_year_eur)} / elev / an`
                : 'Model curent: praguri (legacy)'}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <Input label="Caută școală" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nume școală..." />
        <div className="flex items-end gap-2">
          <div className="text-sm text-muted-foreground">
            Școli: <span className="text-foreground font-medium">{filteredSchools.length}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          Pagină <span className="text-foreground">{Math.min(Math.max(1, page), totalPages)}</span> din{' '}
          <span className="text-foreground">{totalPages}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Înapoi
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Înainte
          </Button>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {filteredSchools.length === 0 ? <div className="text-sm text-muted-foreground">Nu există școli.</div> : null}

        {pagedSchools.map((row) => {
          const isExpanded = expanded[row.school_id] ?? false;
          const annual = row.pricing_model === 'per_student_year' ? Number(row.annual_price_eur || 0) : null;
          return (
            <div key={row.school_id} className="border border-border rounded-lg p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {row.school_name}{' '}
                    <span className="text-xs text-muted-foreground">
                      ({row.county || '—'}, {row.city || '—'})
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Utilizatori activi: <span className="text-foreground">{row.users_count}</span> • Elevi activi:{' '}
                    <span className="text-foreground">{row.students_count}</span> • Elevi (FTE):{' '}
                    <span className="text-foreground">{Number(row.students_fte || 0).toFixed(2)}</span> • Cost anual (pro-rata):{' '}
                    <span className="text-foreground">{fmtEUR(annual)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Info" size={14} className="text-muted-foreground" />
                    Tarif: {fmtEUR(row.price_per_student_year_eur)}/elev/an
                    {row.custom_price_per_student_year_eur !== null && row.custom_price_per_student_year_eur !== undefined ? ' (custom)' : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    • Status: <span className="text-foreground">{row.billing_status}</span>
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                    onClick={() => setExpanded((p) => ({ ...p, [row.school_id]: !isExpanded }))}
                  >
                    Detalii
                  </Button>
                </div>
              </div>

              {isExpanded ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div className="border border-border rounded-lg p-3 bg-muted/20">
                      <div className="text-xs text-muted-foreground">Calcul</div>
                      <div className="text-sm text-foreground mt-1">
                        Elevi activi: <span className="font-medium">{row.students_count}</span>
                      </div>
                      <div className="text-sm text-foreground mt-1">
                        Elevi (FTE / pro-rata): <span className="font-medium">{Number(row.students_fte || 0).toFixed(2)}</span>
                      </div>
                      <div className="text-sm text-foreground mt-1">
                        Preț: <span className="font-medium">{fmtEUR(row.price_per_student_year_eur)}</span> / elev / an
                      </div>
                      <div className="text-sm text-foreground mt-1">
                        Total anual (pro-rata): <span className="font-medium">{fmtEUR(annual)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">~ {fmtEUR(annual ? annual / 12 : null)} / lună</div>
                    </div>

                    <Input
                      label="Preț custom (EUR / elev / an)"
                      value={
                        row.custom_price_per_student_year_eur === null || row.custom_price_per_student_year_eur === undefined
                          ? ''
                          : String(row.custom_price_per_student_year_eur)
                      }
                      onChange={(e) => updateLocalSchool(row.school_id, { custom_price_per_student_year_eur: cleanNumber(e.target.value) })}
                      placeholder={`Implicit: ${row.default_price_per_student_year_eur}`}
                    />
                    <Input
                      label="Email facturare"
                      value={row.billing_email || ''}
                      onChange={(e) => updateLocalSchool(row.school_id, { billing_email: e.target.value || null })}
                      placeholder="office@..."
                    />
                    <Input
                      label="Notă"
                      value={row.note || ''}
                      onChange={(e) => updateLocalSchool(row.school_id, { note: e.target.value || null })}
                      placeholder="Observații..."
                    />
                    <Select
                      label="Activ"
                      value={row.active ? '1' : '0'}
                      options={[
                        { value: '1', label: 'Activ' },
                        { value: '0', label: 'Inactiv' },
                      ]}
                      onChange={(v) => updateLocalSchool(row.school_id, { active: String(v) === '1' })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Select
                      label="Status facturare"
                      value={row.billing_status || 'unbilled'}
                      options={[
                        { value: 'unbilled', label: 'Nefacturat' },
                        { value: 'billed', label: 'Facturat' },
                        { value: 'pending', label: 'Plată în așteptare' },
                        { value: 'paid', label: 'Plătit' },
                      ]}
                      onChange={(v) => updateLocalSchool(row.school_id, { billing_status: (String(v) as any) || 'unbilled' })}
                    />
                    <div className="flex items-end">
                      <div className="text-xs text-muted-foreground">
                        Poți folosi statusul pentru evidență internă (nu afectează calculul).
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Input
                      label="Data începere"
                      type="date"
                      value={row.starts_at || ''}
                      onChange={(e) => updateLocalSchool(row.school_id, { starts_at: e.target.value || null })}
                    />
                    <Input
                      label="Data finalizare"
                      type="date"
                      value={row.ends_at || ''}
                      onChange={(e) => updateLocalSchool(row.school_id, { ends_at: e.target.value || null })}
                    />
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Button variant="default" size="sm" iconName="Save" onClick={() => void saveSchool(row)} disabled={loading}>
                      Salvează
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
